import { create } from "zustand";

export const GOOGLE_CLIENT_ID = "180721207737-66hk4uutpagv7eesaktbqtjteippq2dc.apps.googleusercontent.com";

export interface User { email: string; name: string }

interface AuthState {
  user: User | null;
  ready: boolean;            // /api/auth/me has resolved at least once
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  ready: false,
  refresh: async () => {
    try {
      const r = await fetch("/api/auth/me", { credentials: "same-origin" });
      const d = await r.json();
      set({ user: d.user ?? null, ready: true });
      if (d.user) useLinks.getState().refresh();
    } catch {
      set({ ready: true });
    }
  },
  logout: async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }); } catch { /* ignore */ }
    set({ user: null });
    useLinks.setState({ links: [], loaded: false });
  },
}));

// ---- Google Identity Services ----
declare global { interface Window { google?: any } }
let gisPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("GIS load failed"));
    document.head.appendChild(s);
  });
  return gisPromise;
}

let inited = false;
async function initGoogle() {
  await loadGis();
  if (inited) return;
  inited = true;
  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: async (resp: { credential: string }) => {
      try {
        await fetch("/api/auth/google", {
          method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential: resp.credential }),
        });
        await useAuth.getState().refresh();
      } catch { /* ignore */ }
    },
  });
}

// render the official Google button into el (re-renders if called again)
export async function renderGoogleButton(el: HTMLElement) {
  await initGoogle();
  el.innerHTML = "";
  window.google.accounts.id.renderButton(el, { theme: "filled_blue", size: "large", shape: "pill", text: "signin_with", width: 240 });
}

// ---- saved song links (per account) ----
// Only the YouTube link is stored on the account; a PDF sheet attached to a song stays in this
// device's browser (pdf/pdfStore), so another device shows the link without the sheet.
export interface SavedLink { vid: string; title: string; key: string | null; bpm: number | null; added_at: number }

interface LinksState {
  links: SavedLink[];
  loaded: boolean;
  refresh: () => Promise<void>;
  save: (vid: string) => Promise<boolean>;
  remove: (vid: string) => Promise<void>;
}

export const useLinks = create<LinksState>((set, get) => ({
  links: [],
  loaded: false,
  refresh: async () => {
    try {
      const r = await fetch("/api/links", { credentials: "same-origin" });
      if (r.ok) set({ links: (await r.json()).links, loaded: true });
    } catch { /* offline: keep what we have */ }
  },
  save: async (vid) => {
    try {
      const r = await fetch("/api/links", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ vid }),
      });
      if (!r.ok) return false;
      const link: SavedLink = await r.json();
      set({ links: [link, ...get().links.filter((l) => l.vid !== vid)] });
      return true;
    } catch { return false; }
  },
  remove: async (vid) => {
    set({ links: get().links.filter((l) => l.vid !== vid) });
    try { await fetch(`/api/links/${vid}`, { method: "DELETE", credentials: "same-origin" }); } catch { /* ignore */ }
  },
}));
